import { ConfirmModal } from '@/components/ConfirmModal';
import { StatusModal } from '@/components/StatusModal';
import { PrintTargetModal } from '@/components/sklad/PrintTargetModal';
import { LatkaProduktyModal } from '@/components/vyroba/LatkaProduktyModal';
import { MaterialModal } from '@/components/vyroba/MaterialModal';
import type { ProductionTerm, StriharnaProduct, TermCapacity } from '@/components/vyroba/types';
import { useEscapeKey } from '@/hooks/useEscapeKey';
import { ApiError, apiFetch } from '@/lib/apiFetch';
import { tiskStrihaniA4, tiskneDoFrontyA4, zapamatujCilTiskuA4 } from '@/lib/tiskStrihaniA4';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

/** Barvy stavů zakázky — stejné odstíny jako v tabulce Výroba. */
const STAV_CLASSES: Record<string, string> = {
  Rozpracováno: 'text-danger bg-danger-light',
  'Připraveno k přidělení': 'text-primary bg-primary-50',
  'Přiděleno švadlence': 'text-primary bg-primary-50',
  'Šije se': 'text-warning bg-warning-light',
  Došito: 'text-success bg-success-light',
  Hotovo: 'text-success bg-success-light',
};

/** Sekundy → „0 h 11 min" (tvar, na který je střihárna zvyklá). */
function fmtSewing(sec: number): string {
  const min = Math.round(sec / 60);
  return `${Math.floor(min / 60)} h ${min % 60} min`;
}

const WEEKDAYS = ['Neděle', 'Pondělí', 'Úterý', 'Středa', 'Čtvrtek', 'Pátek', 'Sobota'];

/** Odkazy vpravo nahoře — propojení plánů a timeline. */
const linkBtn =
  'text-[17px] font-bold text-text-secondary border border-border rounded-lg px-6 py-3 hover:bg-surface-hover transition-colors shrink-0';

/** Tlačítko "Materiál" v kartě produktu — sdílené i pro "Materiál 1"/"Materiál 2". */
const MATERIAL_BTN =
  'rounded-md border border-border bg-bg px-3 py-2 text-[15px] md:px-2 md:py-1.5 md:text-[13px] lg:px-3 lg:py-2 lg:text-[15px] font-semibold text-text-secondary hover:border-text-muted transition-colors cursor-pointer';

/** 'YYYY-MM-DD' → 'Pondělí' (nad datum v tabu). */
function fmtWeekday(iso: string): string {
  const [y, m, d] = iso.split('-');
  if (!y || !m || !d) return '';
  return WEEKDAYS[new Date(Number(y), Number(m) - 1, Number(d)).getDay()] ?? '';
}

/** 'YYYY-MM-DD' → 'D.M.' (krátce, pro taby). */
function fmtShort(iso: string): string {
  const [, m, d] = iso.split('-');
  return d && m ? `${Number.parseInt(d, 10)}.${Number.parseInt(m, 10)}.` : iso;
}

/** 'YYYY-MM-DD' → 'Pondělí 27.7.2026'. */
function fmtFull(iso: string): string {
  const [y, m, d] = iso.split('-');
  if (!y || !m || !d) return iso;
  const wd = WEEKDAYS[new Date(Number(y), Number(m) - 1, Number(d)).getDay()] ?? '';
  return `${wd} ${Number(d)}.${Number(m)}.${y}`;
}

/**
 * Střihárna — základní verze. Taby s (aktivními) termíny výroby a na každém
 * produkty, které je potřeba nastřihat (seskupené velikosti + kusy). Kapacita,
 * priority, poznámky a doporučené látky přijdou později.
 */
export function StriharnaPage() {
  const [terms, setTerms] = useState<ProductionTerm[]>([]);
  // Kolik toho na termín zbývá nastříhat — podle toho se prázdné taby schovají.
  const [naplnenost, setNaplnenost] = useState<Record<string, number>>({});
  const [activeTerm, setActiveTerm] = useState<ProductionTerm | null>(null);
  const [products, setProducts] = useState<StriharnaProduct[] | null>(null);
  const [capacity, setCapacity] = useState<TermCapacity | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState<{ product: StriharnaProduct; termDate: string } | null>(
    null,
  );
  const [noteEditing, setNoteEditing] = useState<{
    product: StriharnaProduct;
    termDate: string;
  } | null>(null);
  const [detailProduct, setDetailProduct] = useState<StriharnaProduct | null>(null);
  const [materialProduct, setMaterialProduct] = useState<{
    product: StriharnaProduct;
    which: number;
  } | null>(null);
  const [toast, setToast] = useState<{ msg: string; kind: 'success' | 'error' } | null>(null);
  // Produkty, které kolegyně zaevidovala jako nastříhané (zelené tlačítko).
  const [evidovano, setEvidovano] = useState<Record<string, boolean>>({});
  // Karty, u kterých je zapnuté zobrazení prodaných kusů.
  const [prodaneOpen, setProdaneOpen] = useState<Record<string, boolean>>({});
  // Vyhledávání podle látky napříč všemi termíny — nezávislé na vybraném tabu.
  const [searchQuery, setSearchQuery] = useState('');
  const [searchGroups, setSearchGroups] = useState<
    { term_date: string; products: StriharnaProduct[] }[] | null
  >(null);
  const [searching, setSearching] = useState(false);
  const searchActive = searchQuery.trim().length >= 2;
  // Tisk výsledků hledání na A4 — modal se ptá, kam (běžný tisk / fronta na
  // dílenskou tiskárnu Xerox / jen PDF ke kontrole).
  const [printA4Open, setPrintA4Open] = useState(false);

  const today = new Date().toISOString().slice(0, 10);
  // Termíny řazené podle data — i ty PO termínu, kde ještě něco zbývá
  // nastříhat. Zaostávající resty se nesmí tiše ztratit z dohledu.
  const seRazene = [...terms].sort((a, b) => a.term_date.localeCompare(b.term_date));
  // Termíny, na kterých není co stříhat, se v záložkách vůbec neukazují.
  const tabs = seRazene.filter(
    (t) => (naplnenost[t.term_date] ?? 0) > 0 || activeTerm?.id === t.id,
  );

  // Termíny → taby. Vybere se nejstarší, na kterém je co stříhat (klidně
  // i po termínu — to je přednější než čerstvý termín bez restu), jinak
  // nejbližší budoucí.
  useEffect(() => {
    Promise.all([
      apiFetch<{ terms: ProductionTerm[] }>('/api/vyroba/terms'),
      apiFetch<{ terms: { term_date: string; products: number; qty: number }[] }>(
        '/api/vyroba/striharna/terminy',
      ).catch(() => ({ terms: [] })),
    ])
      .then(([d, prehled]) => {
        setTerms(d.terms);
        const obsazeno: Record<string, number> = {};
        for (const p of prehled.terms) obsazeno[p.term_date] = p.products;
        setNaplnenost(obsazeno);
        const t = new Date().toISOString().slice(0, 10);
        const serazene = [...d.terms].sort((a, b) => a.term_date.localeCompare(b.term_date));
        setActiveTerm(
          serazene.find((x) => (obsazeno[x.term_date] ?? 0) > 0) ??
            serazene.find((x) => x.term_date >= t) ??
            serazene[0] ??
            null,
        );
      })
      .catch((e: unknown) =>
        setError(e instanceof ApiError ? e.message : 'Chyba načítání termínů'),
      );
  }, []);

  // Produkty vybraného termínu. Rychlé přeskočení mezi taby umí rozeběhnout
  // dva požadavky najednou — bez zrušení té starší by pomalejší odpověď
  // (klidně za tu předchozí záložku) mohla dorazit později a přepsat čerstvě
  // zobrazený termín daty z jiného data.
  useEffect(() => {
    if (!activeTerm) return;
    let cancelled = false;
    const term = activeTerm;
    setProducts(null);
    setCapacity(null);
    setProdaneOpen({});
    apiFetch<{ products: StriharnaProduct[]; capacity: TermCapacity }>(
      `/api/vyroba/striharna?term_date=${term.term_date}`,
    )
      .then((d) => {
        if (cancelled) return;
        // Backend vrací kartu bez "prodáno" (nejdražší část — živý Shoptet
        // dotaz) — sold rovnou zahodíme, ať se karta nezobrazí s nesmyslnou
        // nulou. Dorazí to o chvíli později druhým requestem níž.
        setProducts(
          d.products.map((p) => ({
            ...p,
            variants: p.variants.map((v) => ({ ...v, sold: undefined })),
          })),
        );
        setCapacity(d.capacity);

        apiFetch<{ sold: Record<string, number> }>(
          `/api/vyroba/striharna/prodano?term_date=${term.term_date}`,
        )
          .then((sd) => {
            if (cancelled) return;
            setProducts((prev) =>
              prev
                ? prev.map((p) => ({
                    ...p,
                    variants: p.variants.map((v) => ({
                      ...v,
                      sold: sd.sold[`${p.product_code}|${v.variant_name ?? ''}`] ?? 0,
                    })),
                  }))
                : prev,
            );
          })
          .catch(() => {
            /* tichá chyba — karty zůstanou, jen "prodáno" nedonačte */
          });
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        setProducts([]);
        setError(e instanceof ApiError ? e.message : 'Chyba načítání produktů');
      });
    return () => {
      cancelled = true;
    };
  }, [activeTerm]);

  // Hledání podle látky — debounced, hledá napříč VŠEMI termíny najednou.
  useEffect(() => {
    const q = searchQuery.trim();
    if (q.length < 2) {
      setSearchGroups(null);
      setSearching(false);
      return;
    }
    setSearching(true);
    const handle = setTimeout(() => {
      apiFetch<{ groups: { term_date: string; products: StriharnaProduct[] }[] }>(
        `/api/vyroba/striharna/search?q=${encodeURIComponent(q)}`,
      )
        .then((d) => setSearchGroups(d.groups))
        .catch((e: unknown) => {
          setSearchGroups([]);
          setToast({
            msg: e instanceof ApiError ? e.describe() : 'Vyhledávání selhalo',
            kind: 'error',
          });
        })
        .finally(() => setSearching(false));
    }, 300);
    return () => clearTimeout(handle);
  }, [searchQuery]);

  // Stejné řazení jako na backendu: prioritní nahoru, pak látky u sebe.
  function resort(list: StriharnaProduct[]): StriharnaProduct[] {
    const perFabric = new Map<string, number>();
    for (const p of list) perFabric.set(p.fabric, (perFabric.get(p.fabric) ?? 0) + 1);
    return [...list].sort(
      (a, b) =>
        Number(b.priority) - Number(a.priority) ||
        (perFabric.get(b.fabric) ?? 0) - (perFabric.get(a.fabric) ?? 0) ||
        a.fabric.localeCompare(b.fabric, 'cs') ||
        a.product_name.localeCompare(b.product_name),
    );
  }

  /**
   * Upraví produkt všude, kde je zrovna vidět — v tabu aktivního termínu i ve
   * skupině výsledků hledání se stejným termínem. Karta stejné zakázky se totiž
   * může zobrazovat na obou místech zároveň.
   */
  function updateProductEverywhere(
    termDate: string,
    code: string,
    updater: (p: StriharnaProduct) => StriharnaProduct,
    doResort: boolean,
  ) {
    if (activeTerm?.term_date === termDate) {
      setProducts((prev) => {
        if (!prev) return prev;
        const next = prev.map((p) => (p.product_code === code ? updater(p) : p));
        return doResort ? resort(next) : next;
      });
    }
    setSearchGroups((prev) =>
      prev
        ? prev.map((g) => {
            if (g.term_date !== termDate) return g;
            const next = g.products.map((p) => (p.product_code === code ? updater(p) : p));
            return { ...g, products: doResort ? resort(next) : next };
          })
        : prev,
    );
  }

  // Nastaví/odebere prioritu — optimisticky přeřadí a zavolá backend.
  async function togglePriority(product: StriharnaProduct, termDate: string) {
    setPending(null);
    const next = !product.priority;
    updateProductEverywhere(
      termDate,
      product.product_code,
      (p) => ({ ...p, priority: next }),
      true,
    );
    try {
      await apiFetch('/api/vyroba/striharna/priority', {
        method: 'POST',
        body: { term_date: termDate, product_code: product.product_code, priority: next },
      });
    } catch (e: unknown) {
      updateProductEverywhere(
        termDate,
        product.product_code,
        (p) => ({ ...p, priority: product.priority }),
        true,
      ); // revert
      setToast({
        msg: e instanceof ApiError ? e.describe() : 'Změna priority selhala',
        kind: 'error',
      });
    }
  }

  // Uloží/smaže poznámku — optimisticky updatne produkt a zavolá backend.
  async function saveNote(product: StriharnaProduct, text: string, termDate: string) {
    setNoteEditing(null);
    const trimmed = text.trim();
    const note = trimmed.length > 0 ? trimmed : null;
    updateProductEverywhere(termDate, product.product_code, (p) => ({ ...p, note }), false);
    try {
      await apiFetch('/api/vyroba/striharna/note', {
        method: 'POST',
        body: { term_date: termDate, product_code: product.product_code, note: trimmed },
      });
    } catch (e: unknown) {
      updateProductEverywhere(
        termDate,
        product.product_code,
        (p) => ({ ...p, note: product.note }),
        false,
      ); // revert
      setToast({
        msg: e instanceof ApiError ? e.describe() : 'Uložení poznámky selhalo',
        kind: 'error',
      });
    }
  }

  /** Nastříháno → do fronty „Rozpracované zakázky k dokončení" ve Výrobě. */
  async function zaevidovat(product: StriharnaProduct, termDate: string) {
    try {
      await apiFetch('/api/vyroba/striharna/zaevidovat', {
        method: 'POST',
        body: { term_date: termDate, product_code: product.product_code },
      });
      setEvidovano((p) => ({ ...p, [product.product_code]: true }));
      setToast({ msg: `${product.product_code} zaevidován k dokončení`, kind: 'success' });
    } catch (e: unknown) {
      setToast({
        msg: e instanceof ApiError ? e.describe() : 'Zaevidování selhalo',
        kind: 'error',
      });
    }
  }

  /** Lupa u tlačítka "Materiál" — hledání se zobrazuje nad kartami, proto skok nahoru. */
  function searchByMaterial(name: string) {
    setSearchQuery(name);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  return (
    <>
      <div className="flex items-start justify-between gap-4 mb-6 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-text">Střihárna</h1>
          <p className="text-sm text-text-secondary mt-0.5 max-w-2xl">
            Co je potřeba nastřihat k jednotlivým termínům výroby. Vyber termín v záložkách.
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Link to="/vyroba/sklad-latek" title="Role látek na skladě" className={linkBtn}>
            Sklad látek
          </Link>
          <Link
            to="/vyroba/striharna/timeline"
            title="Kdo právě šije a kolik mu zbývá"
            className={linkBtn}
          >
            Timeline
          </Link>
          <Link
            to="/vyroba/dokoncovaci-dilna"
            title="Co je předskladněné pro dokončovací dílnu"
            className={linkBtn}
          >
            Dokončovací dílna
          </Link>
        </div>
      </div>

      {error && (
        <div className="text-sm text-danger bg-danger-light border border-danger/30 rounded-lg px-4 py-3 mb-4">
          {error}
        </div>
      )}

      {/* Taby s termíny */}
      {tabs.length > 0 ? (
        <div className="flex flex-wrap gap-2 mb-5">
          {tabs.map((t) => {
            const active = activeTerm?.id === t.id;
            const poTerminu = t.term_date < today;
            return (
              <button
                key={t.id}
                type="button"
                onClick={() => {
                  // Vymazat rovnou při kliku, ne až v efektu po přepnutí
                  // termínu — ať se skeleton objeví v okamžiku kliknutí.
                  setProducts(null);
                  setActiveTerm(t);
                }}
                // Větší terč na dotyk — hlavní ovládání na tabletu.
                className={`relative rounded-lg px-6 py-3 text-[17px] font-bold transition-colors cursor-pointer ${
                  active
                    ? 'bg-action text-white'
                    : poTerminu
                      ? 'bg-danger-light border border-danger/40 text-danger hover:border-danger'
                      : 'bg-bg border border-border text-text-secondary hover:border-text-muted'
                }`}
              >
                {poTerminu && (
                  <span className="absolute -top-2 -right-2 text-[10px] font-bold uppercase tracking-wide bg-danger text-white rounded px-1.5 py-0.5 whitespace-nowrap">
                    Po termínu
                  </span>
                )}
                <span className="flex flex-col leading-tight">
                  <span
                    className={`text-[14px] font-semibold ${active ? 'text-white/80' : poTerminu ? 'text-danger/80' : 'text-text-muted'}`}
                  >
                    {fmtWeekday(t.term_date)}
                  </span>
                  {t.is_service ? '⚙️ ' : ''}
                  {fmtShort(t.term_date)}
                </span>
              </button>
            );
          })}
        </div>
      ) : null}
      {tabs.some((t) => t.is_service) && (
        <p className="text-xs text-text-muted mb-5 -mt-3">
          ⚙️ - Servisní termín pouze pro chyby ve skladu
        </p>
      )}
      {tabs.length === 0 && (
        <p className="text-sm text-text-muted">
          Žádné aktivní termíny výroby. Nejdřív je založ v sekci „Termíny výroby".
        </p>
      )}

      {/* Hledání podle látky — napříč všemi termíny, i budoucími. */}
      <div className="flex items-stretch gap-3 mb-5">
        <div className="relative max-w-xl flex-1">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
            className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-text-muted pointer-events-none"
          >
            <circle cx="11" cy="11" r="8" />
            <path d="M21 21l-4.35-4.35" />
          </svg>
          <input
            type="search"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder='Hledat podle látky (např. „reflex")…'
            className="w-full bg-white border border-border rounded-lg pl-11 pr-10 py-3.5 text-lg text-text shadow-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
          />
          {searchQuery && (
            <button
              type="button"
              onClick={() => setSearchQuery('')}
              aria-label="Vymazat hledání"
              className="absolute right-3.5 top-1/2 -translate-y-1/2 text-text-muted hover:text-text-secondary text-2xl leading-none cursor-pointer"
            >
              ×
            </button>
          )}
        </div>
        {searchQuery && (
          <button
            type="button"
            onClick={() => setSearchQuery('')}
            className="shrink-0 rounded-lg bg-danger hover:opacity-90 text-white font-bold px-5 text-lg cursor-pointer transition-opacity"
          >
            Zrušit filtr
          </button>
        )}
        {searchQuery && searchGroups != null && searchGroups.length > 0 && (
          <button
            type="button"
            onClick={() => setPrintA4Open(true)}
            title="Vytisknout přehled i souhrn kusů napříč termíny na A4"
            className="shrink-0 rounded-lg bg-primary hover:bg-primary-dark text-white font-bold px-5 text-lg cursor-pointer transition-colors"
          >
            Vytisknout A4
          </button>
        )}
      </div>

      {printA4Open && searchGroups && (
        <PrintTargetModal
          heading="Kam vytisknout plán?"
          title={`Látka „${searchQuery.trim()}" — přehled i souhrn na A4`}
          vychoziFronta={tiskneDoFrontyA4()}
          queueLabel="Xerox v dílně"
          queueDescription="Dokument se zařadí do fronty a vytiskne se na dílenské tiskárně."
          onPick={(target) => {
            setPrintA4Open(false);
            if (target !== 'pdf') zapamatujCilTiskuA4(target === 'fronta');
            tiskStrihaniA4(searchGroups, searchQuery.trim(), target);
          }}
          onClose={() => setPrintA4Open(false)}
        />
      )}

      {searchActive ? (
        <>
          {searching && <p className="text-sm text-text-muted px-1 mb-3">Hledám…</p>}
          {!searching && searchGroups != null && searchGroups.length === 0 && (
            <p className="text-sm text-text-muted px-1">
              Žádná naplánovaná zakázka z látky „{searchQuery.trim()}" se nenašla.
            </p>
          )}
          {searchGroups?.map((g) => (
            <div key={g.term_date} className="mb-6">
              <h2 className="text-xl font-bold text-text mb-3">{fmtFull(g.term_date)}</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {g.products.map((p) => (
                  <ProductCard
                    key={`${g.term_date}-${p.product_code}`}
                    p={p}
                    termDate={g.term_date}
                    evidovano={!!evidovano[p.product_code]}
                    onPriority={() => setPending({ product: p, termDate: g.term_date })}
                    onNote={() => setNoteEditing({ product: p, termDate: g.term_date })}
                    onDetail={() => setDetailProduct(p)}
                    onMaterial={(which) => setMaterialProduct({ product: p, which })}
                    onSearchMaterial={searchByMaterial}
                    prodaneOn={!!prodaneOpen[p.product_code]}
                    onProdane={() =>
                      setProdaneOpen((prev) => ({
                        ...prev,
                        [p.product_code]: !prev[p.product_code],
                      }))
                    }
                    onZaevidovat={() => zaevidovat(p, g.term_date)}
                  />
                ))}
              </div>
            </div>
          ))}
        </>
      ) : (
        /* Obsah vybraného termínu */
        activeTerm && (
          <>
            <div className="bg-surface rounded-xl shadow-card px-5 py-4 mb-5">
              <h2 className="text-xl font-bold text-text">{fmtFull(activeTerm.term_date)}</h2>
              {capacity && <CapacityBox c={capacity} />}
            </div>

            {products == null ? (
              <ProductGridSkeleton />
            ) : products.length === 0 ? (
              <p className="text-sm text-text-muted px-1">
                K tomuto termínu není zatím zařazený žádný produkt.
              </p>
            ) : (
              // Na iPadu (od 768 px) dvě karty vedle sebe, ne jedna přes celou šířku.
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {products.map((p) => (
                  <ProductCard
                    key={p.product_code}
                    p={p}
                    termDate={activeTerm.term_date}
                    evidovano={!!evidovano[p.product_code]}
                    onPriority={() => setPending({ product: p, termDate: activeTerm.term_date })}
                    onNote={() => setNoteEditing({ product: p, termDate: activeTerm.term_date })}
                    onDetail={() => setDetailProduct(p)}
                    onMaterial={(which) => setMaterialProduct({ product: p, which })}
                    onSearchMaterial={searchByMaterial}
                    prodaneOn={!!prodaneOpen[p.product_code]}
                    onProdane={() =>
                      setProdaneOpen((prev) => ({
                        ...prev,
                        [p.product_code]: !prev[p.product_code],
                      }))
                    }
                    onZaevidovat={() => zaevidovat(p, activeTerm.term_date)}
                  />
                ))}
              </div>
            )}
          </>
        )
      )}

      {pending && (
        <ConfirmModal
          title={pending.product.priority ? 'Odebrat prioritu?' : 'Nastavit prioritu?'}
          message={
            pending.product.priority
              ? `Produkt ${pending.product.product_code} přestane být přednostní.`
              : `Produkt ${pending.product.product_code} se přesune na první místo v termínu.`
          }
          confirmText={pending.product.priority ? 'Odebrat prioritu' : 'Nastavit prioritu'}
          confirmKind={pending.product.priority ? 'danger' : 'primary'}
          onConfirm={() => togglePriority(pending.product, pending.termDate)}
          onCancel={() => setPending(null)}
        />
      )}
      {toast && (
        <StatusModal message={toast.msg} kind={toast.kind} onClose={() => setToast(null)} />
      )}
      {detailProduct && (
        <LatkaProduktyModal
          productCode={detailProduct.product_code}
          onClose={() => setDetailProduct(null)}
        />
      )}
      {materialProduct && (
        <MaterialModal
          productCode={materialProduct.product.product_code}
          which={materialProduct.product.materials.length >= 2 ? materialProduct.which : undefined}
          onClose={() => setMaterialProduct(null)}
        />
      )}
      {noteEditing && (
        <NoteModal
          productCode={noteEditing.product.product_code}
          initial={noteEditing.product.note ?? ''}
          onSave={(text) => saveNote(noteEditing.product, text, noteEditing.termDate)}
          onCancel={() => setNoteEditing(null)}
        />
      )}
    </>
  );
}

/** Zástupné karty při přepnutí termínu — kopírují rozvržení skutečných karet. */
function ProductGridSkeleton() {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {Array.from({ length: 4 }, (_, i) => (
        // biome-ignore lint/suspicious/noArrayIndexKey: statický počet, nic se nepřeuspořádává
        <div key={i} className="bg-surface rounded-xl shadow-card p-5 animate-pulse">
          <div className="flex gap-5">
            <div className="w-[136px] h-[136px] rounded-lg bg-bg shrink-0" />
            <div className="min-w-0 flex-1 space-y-2.5">
              <div className="h-3.5 w-20 rounded bg-bg" />
              <div className="h-5 w-3/4 rounded bg-bg" />
              <div className="h-3.5 w-1/2 rounded bg-bg" />
              <div className="mt-3 space-y-2">
                <div className="h-4 w-full rounded bg-bg" />
                <div className="h-4 w-full rounded bg-bg" />
                <div className="h-4 w-full rounded bg-bg" />
              </div>
            </div>
          </div>
          <div className="mt-4 flex gap-2">
            <div className="h-9 flex-1 rounded-md bg-bg" />
            <div className="h-9 flex-1 rounded-md bg-bg" />
          </div>
        </div>
      ))}
    </div>
  );
}

/** Sekundy → „19 h 28 min" (záporné se ukáže s minusem). */
function fmtHm(sec: number): string {
  const sign = sec < 0 ? '−' : '';
  const total = Math.round(Math.abs(sec) / 60);
  return `${sign}${Math.floor(total / 60)} h ${total % 60} min`;
}

/**
 * Souhrn kapacity termínu — kolik hodin dílna do termínu ještě má, co je na
 * něj naplánované a kolik zbývá. Nad doporučeným využitím zežloutne, nad
 * stoprocentním zčervená.
 */
function CapacityBox({ c }: { c: TermCapacity }) {
  const over = c.utilization_pct > 100;
  const warn = !over && c.utilization_pct > c.recommended_pct;
  const border = over ? 'border-danger' : warn ? 'border-warning' : 'border-action';
  const pct = over ? 'text-danger' : warn ? 'text-warning' : 'text-action';
  return (
    <div className={`mt-3 border-l-4 ${border} bg-bg rounded-r-lg px-4 py-3 text-[15px]`}>
      <div>
        <strong className="font-bold text-text">Kapacita:</strong>{' '}
        <span className="text-text-secondary">
          {fmtHm(c.hours * 3600)}, {c.workers} švadlenek, doporučené max. využití:{' '}
          {c.recommended_pct} %, poslední den jen do 10:00
        </span>
      </div>
      <div>
        <strong className="font-bold text-text">Naplánováno:</strong>{' '}
        <span className="text-text-secondary">{fmtHm(c.planned_sec)} na tento termín</span>
        {c.planned_total_sec > c.planned_sec && (
          <span className="text-text-secondary">
            {' '}
            · <strong className="font-semibold text-text">{fmtHm(c.planned_total_sec)}</strong>{' '}
            včetně dřívějších termínů
          </span>
        )}
      </div>
      <div>
        <strong className="font-bold text-text">Rezerva:</strong>{' '}
        <span className={c.reserve_sec < 0 ? 'font-semibold text-danger' : 'text-text-secondary'}>
          {fmtHm(c.reserve_sec)}
        </span>
      </div>
      <div>
        <strong className="font-bold text-text">Využití:</strong>{' '}
        <span className={`font-bold ${pct}`}>{Math.round(c.utilization_pct)} %</span>
      </div>
    </div>
  );
}

function ProductCard({
  p,
  termDate,
  evidovano,
  onPriority,
  onNote,
  onDetail,
  onMaterial,
  onSearchMaterial,
  prodaneOn,
  onProdane,
  onZaevidovat,
}: {
  p: StriharnaProduct;
  /** Termín, na který se právě dívá — u stejné látky pak odlišíme „tento termín". */
  termDate: string;
  /** Už je ve frontě k dokončení — tlačítko zšedne, ať se neklikne dvakrát. */
  evidovano: boolean;
  onPriority: () => void;
  onNote: () => void;
  onDetail: () => void;
  /** `which` = 1-based pořadí látky (u dvou látek zvlášť "Materiál 1"/"Materiál 2"). */
  onMaterial: (which: number) => void;
  /** Lupa u tlačítka Materiál — najde všechny naplánované produkty z týž látky. */
  onSearchMaterial: (fabricName: string) => void;
  /** Ukazovat u velikostí, kolik kusů je už prodaných. */
  prodaneOn: boolean;
  onProdane: () => void;
  onZaevidovat: () => void;
}) {
  return (
    // Vnitřek karty je o 30 % větší (čitelnost na dílně), šířka karty se nemění —
    // mřížka zůstává dvousloupcová.
    // Nastříhaná karta má vlastní jemně zelený podklad (mimo paletu tokenů).
    <div
      className={`rounded-xl shadow-card p-5 ${
        p.done ? 'bg-[#edffec] border border-action/30' : 'bg-surface'
      }`}
    >
      {/* Přepínač vpravo nahoře — dotáhne, kolik kusů je z termínu prodaných. */}
      <div className="flex justify-end -mt-1 -mr-1 mb-1">
        {!p.done && (
          <button
            type="button"
            onClick={onProdane}
            className={`text-[13px] font-semibold cursor-pointer transition-colors ${
              prodaneOn ? 'text-warning' : 'text-text-muted hover:text-text-secondary'
            }`}
          >
            {prodaneOn ? 'Skrýt prodané' : 'Ukázat jen prodané'}
          </button>
        )}
      </div>
      {p.note && (
        <div className="mb-4 rounded-md border-l-4 border-warning bg-warning-light px-4 py-2.5 text-lg text-text whitespace-pre-wrap">
          {p.note}
        </div>
      )}
      <div className="flex gap-5 md:gap-3 lg:gap-5">
        {/* Na iPadu je levý sloupec o ~15 % užší, ať zbyde víc místa na velikosti. */}
        <div className="shrink-0 flex flex-col gap-2.5 md:gap-2 lg:gap-2.5">
          {p.image_url ? (
            <img
              src={p.image_url}
              alt=""
              className="w-[104px] h-[104px] md:w-[88px] md:h-[88px] lg:w-[104px] lg:h-[104px] object-contain rounded-lg border border-border bg-white p-1"
            />
          ) : (
            <div className="w-[104px] h-[104px] md:w-[88px] md:h-[88px] lg:w-[104px] lg:h-[104px] rounded-lg border border-border bg-bg" />
          )}
          {p.materials.length >= 2 ? (
            <>
              <div className="flex items-stretch gap-1.5">
                <button
                  type="button"
                  onClick={() => onMaterial(1)}
                  title="Role první látky na skladě a co je objednané"
                  className={`${MATERIAL_BTN} flex-1 flex items-center justify-between gap-1.5`}
                >
                  {/* Kamion = na látku je objednávka, která ještě nedorazila. */}
                  <span>{p.material_ordered ? '🚚 Materiál 1' : 'Materiál 1'}</span>
                  {p.materials[0]?.image && (
                    <img
                      src={p.materials[0].image}
                      alt=""
                      className="w-6 h-6 shrink-0 rounded-md border border-border object-cover"
                    />
                  )}
                </button>
                {p.materials[0]?.has_other_products && (
                  <MaterialSearchButton name={p.materials[0].name} onSearch={onSearchMaterial} />
                )}
              </div>
              <div className="flex items-stretch gap-1.5">
                <button
                  type="button"
                  onClick={() => onMaterial(2)}
                  title="Role druhé látky na skladě a co je objednané"
                  className={`${MATERIAL_BTN} flex-1 flex items-center justify-between gap-1.5`}
                >
                  <span>{p.material_ordered ? '🚚 Materiál 2' : 'Materiál 2'}</span>
                  {p.materials[1]?.image && (
                    <img
                      src={p.materials[1].image}
                      alt=""
                      className="w-6 h-6 shrink-0 rounded-md border border-border object-cover"
                    />
                  )}
                </button>
                {p.materials[1]?.has_other_products && (
                  <MaterialSearchButton name={p.materials[1].name} onSearch={onSearchMaterial} />
                )}
              </div>
            </>
          ) : (
            <div className="flex items-stretch gap-1.5">
              <button
                type="button"
                onClick={() => onMaterial(1)}
                title="Role látky na skladě a co je objednané"
                className={`${MATERIAL_BTN} flex-1`}
              >
                {/* Kamion = na látku je objednávka, která ještě nedorazila. */}
                {p.material_ordered ? '🚚 Materiál' : 'Materiál'}
              </button>
              {p.materials[0]?.has_other_products && (
                <MaterialSearchButton name={p.materials[0].name} onSearch={onSearchMaterial} />
              )}
            </div>
          )}
          <button
            type="button"
            onClick={onPriority}
            className={`rounded-md border px-3 py-2 text-[15px] md:px-2 md:py-1.5 md:text-[13px] lg:px-3 lg:py-2 lg:text-[15px] font-semibold transition-colors cursor-pointer ${
              p.priority
                ? 'border-danger/30 bg-danger-light text-danger hover:opacity-90'
                : 'border-border bg-bg text-text-secondary hover:border-text-muted'
            }`}
          >
            {p.priority ? 'Odebrat prioritu' : 'Nastavit prioritu'}
          </button>
          <button
            type="button"
            onClick={onNote}
            className={`rounded-md border px-3 py-2 text-[15px] md:px-2 md:py-1.5 md:text-[13px] lg:px-3 lg:py-2 lg:text-[15px] font-semibold transition-colors cursor-pointer ${
              p.note
                ? 'border-warning/40 bg-warning-light text-warning hover:opacity-90'
                : 'border-border bg-bg text-text-secondary hover:border-text-muted'
            }`}
          >
            📝 Poznámka
          </button>
          <button
            type="button"
            onClick={onDetail}
            title="Co dalšího se šije ze stejné látky"
            className="rounded-md border border-border bg-bg px-3 py-2 text-[15px] md:px-2 md:py-1.5 md:text-[13px] lg:px-3 lg:py-2 lg:text-[15px] font-semibold text-text-secondary hover:border-text-muted transition-colors cursor-pointer"
          >
            Detail
          </button>
          <button
            type="button"
            onClick={onZaevidovat}
            disabled={evidovano}
            title="Nastříháno — pošli do fronty k založení zakázky"
            className={`rounded-md px-3 py-2 text-[15px] md:px-2 md:py-1.5 md:text-[13px] lg:px-3 lg:py-2 lg:text-[15px] font-semibold transition-colors ${
              evidovano
                ? 'border border-border bg-bg text-text-muted cursor-default'
                : 'bg-action text-white hover:bg-action-dark cursor-pointer'
            }`}
          >
            {evidovano ? '✓ Zaevidováno' : 'Zaevidovat'}
          </button>
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="text-[15px] font-mono text-text-muted">{p.product_code}</span>
            {p.priority && (
              <span className="inline-flex items-center justify-center w-[26px] h-[26px] rounded-full bg-danger text-white text-[15px] font-bold animate-pulse">
                !
              </span>
            )}
          </div>
          <div className="text-lg font-semibold text-text">{p.product_name}</div>
          {p.fabric && (
            <div className="mt-1 text-[15px]">
              <span className="font-semibold text-text">Látka: </span>
              <span className="text-text-secondary">{p.fabric}</span>
            </div>
          )}
          {p.naplet && (
            <div className="mt-1 text-[15px]">
              <span className="font-semibold text-text">Náplet: </span>
              <span
                className="inline-block rounded-full px-2.5 py-0.5 text-sm font-semibold align-middle"
                style={{ backgroundColor: p.naplet.bg, color: p.naplet.fg }}
              >
                {p.naplet.barva}
              </span>
            </div>
          )}
          {p.sewing_sec > 0 && (
            <div className="text-[15px] text-text">
              <span className="font-semibold">Čas šití: </span>
              {fmtSewing(p.sewing_sec)}
            </div>
          )}
          {p.done ? (
            <div className="mt-3 flex items-center gap-2 text-action-dark">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="26"
                height="26"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="3"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <path d="M20 6 9 17l-5-5" />
              </svg>
              <span className="text-lg font-bold">Kompletně nastříháno</span>
            </div>
          ) : (
            <div className="mt-2.5">
              {p.variants.map((v) => (
                <div
                  key={v.variant_name ?? '__none__'}
                  className="flex items-center justify-between border-b border-border-light py-1.5 text-base"
                >
                  <span className="text-text">{v.variant_name ?? 'Bez velikosti'}</span>
                  <span className="flex items-baseline gap-2">
                    {prodaneOn &&
                      (v.sold == null ? (
                        <span className="inline-block h-4 w-20 rounded bg-warning-light animate-pulse" />
                      ) : (
                        <span className="text-[15px] font-semibold text-warning">
                          prodáno {v.sold} ks
                        </span>
                      ))}
                    <span className="font-semibold text-text">{v.qty} ks</span>
                  </span>
                </div>
              ))}
            </div>
          )}
          {!p.done && (
            <div className="mt-2 text-[15px] text-text-muted">Celkem {p.total_qty} ks</div>
          )}
        </div>
      </div>
      {p.open_orders.length > 0 && (
        <div className="mt-2">
          {p.open_orders.map((o) => (
            <div
              key={o.id_zakazky}
              className="flex items-center gap-3 text-[15px] flex-wrap py-0.5"
            >
              <span className="font-semibold text-text w-16 shrink-0">{o.id_zakazky}</span>
              <span className="text-text-secondary w-28 shrink-0">
                {o.svadlenka || 'nepřidělena'}
              </span>
              {o.stav && (
                <span
                  className={`text-sm font-semibold px-2 py-0.5 rounded ${
                    STAV_CLASSES[o.stav] ?? 'text-text-secondary bg-bg'
                  }`}
                >
                  {o.stav}
                </span>
              )}
            </div>
          ))}
        </div>
      )}
      {p.same_fabric.length > 0 && (
        <div className="mt-3 pt-3 border-t border-border-light">
          <div className="text-[15px] font-semibold text-text">Další produkty z této látky:</div>
          <ul className="mt-1 space-y-0.5">
            {p.same_fabric.map((s) => (
              <li key={`${s.product_code}-${s.term_date}`} className="text-[15px] text-text">
                <span className="font-mono text-text-muted">{s.product_code}</span>{' '}
                <span className={s.term_date === termDate ? 'text-action' : 'text-text-secondary'}>
                  ({s.term_date === termDate ? 'tento termín' : fmtShort(s.term_date)})
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

/** Lupa vedle tlačítka Materiál — najde napříč termíny vše z téže látky. */
function MaterialSearchButton({
  name,
  onSearch,
}: {
  name: string;
  onSearch: (fabricName: string) => void;
}) {
  return (
    <button
      type="button"
      onClick={() => onSearch(name)}
      title={`Najít další produkty z látky „${name}"`}
      aria-label={`Najít další produkty z látky ${name}`}
      className="shrink-0 rounded-md border border-border bg-bg px-2.5 text-text-secondary hover:border-text-muted hover:text-text transition-colors cursor-pointer"
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
        className="w-4 h-4"
      >
        <circle cx="11" cy="11" r="8" />
        <path d="M21 21l-4.35-4.35" />
      </svg>
    </button>
  );
}

function NoteModal({
  productCode,
  initial,
  onSave,
  onCancel,
}: {
  productCode: string;
  initial: string;
  onSave: (text: string) => void;
  onCancel: () => void;
}) {
  const [text, setText] = useState(initial);
  useEscapeKey(onCancel);
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <button
        type="button"
        aria-label="Zavřít"
        onClick={onCancel}
        className="absolute inset-0 bg-black/30 backdrop-blur-sm cursor-default"
      />
      <div className="relative w-full max-w-md bg-surface rounded-2xl shadow-modal p-6 reveal">
        <h2 className="text-lg font-bold text-text mb-3">
          Poznámka <span className="font-mono text-sm text-text-muted">{productCode}</span>
        </h2>
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          rows={4}
          placeholder="Napiš poznámku…"
          className="w-full border border-border rounded-lg px-3 py-2 text-sm text-text focus:outline-none focus:ring-2 focus:ring-primary/30 resize-y"
        />
        <div className="flex items-center justify-between gap-3 mt-4">
          {initial.trim() ? (
            <button
              type="button"
              onClick={() => onSave('')}
              className="text-sm font-semibold text-danger hover:opacity-80 px-2 py-2 cursor-pointer"
            >
              Odstranit poznámku
            </button>
          ) : (
            <span />
          )}
          <div className="flex gap-3">
            <button
              type="button"
              onClick={onCancel}
              className="text-sm font-semibold text-text-secondary hover:text-text px-4 py-2 cursor-pointer"
            >
              Zrušit
            </button>
            <button
              type="button"
              onClick={() => onSave(text)}
              className="bg-action hover:bg-action-dark text-white text-sm font-bold rounded-lg px-5 py-2 cursor-pointer transition-colors"
            >
              Uložit
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
